import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUsers } from '@/hooks/useUsers'
import { useEmailTemplates } from '@/hooks/useEmailTemplates'
import { emailCampaignsService, type EmailCampaign } from '@/services/emailCampaignsService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Mail, Send, Clock, CheckCircle, XCircle, Search } from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export function EmailCampaignsPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [page, setPage] = useState(1)

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['emailCampaigns', page],
    queryFn: () => emailCampaignsService.listCampaigns(page, 10),
  })

  const campaigns = campaignsData?.campaigns || []

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold">Campañas de Email</h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Campañas de Email</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Envía campañas masivas de email a tus usuarios
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Send className="mr-2 h-4 w-4" />
          Nueva Campaña
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No hay campañas enviadas aún</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
              Crear primera campaña
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <CampaignCard key={campaign._id} campaign={campaign} />
          ))}

          {campaignsData && campaignsData.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {page} de {campaignsData.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page === campaignsData.total_pages}
              >
                Siguiente
              </Button>
            </div>
          )}
        </div>
      )}

      <CreateCampaignDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
    </div>
  )
}

// ========================================
// Campaign Card
// ========================================

function CampaignCard({ campaign }: { campaign: EmailCampaign }) {
  const statusConfig = {
    processing: {
      icon: Clock,
      label: 'Procesando',
      variant: 'secondary' as const,
      color: 'text-blue-600',
    },
    completed: {
      icon: CheckCircle,
      label: 'Completado',
      variant: 'outline' as const,
      color: 'text-green-600',
    },
    failed: {
      icon: XCircle,
      label: 'Fallido',
      variant: 'error' as const,
      color: 'text-red-600',
    },
  }

  const config = statusConfig[campaign.status]
  const StatusIcon = config.icon

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base">{campaign.name}</CardTitle>
            <p className="text-xs text-muted-foreground">{campaign.template_subject}</p>
          </div>
          <Badge variant={config.variant} className="flex items-center gap-1">
            <StatusIcon className={`h-3 w-3 ${config.color}`} />
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Total</p>
            <p className="font-medium">{campaign.stats.total}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Enviados</p>
            <p className="font-medium text-green-600">{campaign.stats.sent}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Fallidos</p>
            <p className="font-medium text-red-600">{campaign.stats.failed}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Fecha</p>
            <p className="font-medium">
              {format(new Date(campaign.created_at), 'd MMM yy', { locale: es })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ========================================
// Create Campaign Dialog
// ========================================

function CreateCampaignDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [userPage, setUserPage] = useState(1)

  // Búsqueda server-side con debounce (escala a bases de usuarios grandes;
  // antes se cargaban TODOS los usuarios — máx 100 — y se filtraban en cliente).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
      setUserPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchQuery])

  const { data: usersData, isLoading: usersLoading } = useUsers({
    role: 'platform',
    search: debouncedSearch || undefined,
    page: userPage,
    limit: 50,
  })
  const { data: templatesData, isLoading: templatesLoading } = useEmailTemplates()

  const createMutation = useMutation({
    mutationFn: emailCampaignsService.createCampaign,
    onSuccess: () => {
      toast.success('Campaña creada exitosamente')
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] })
      handleClose()
    },
    onError: () => {
      toast.error('Error al crear la campaña')
    },
  })

  const handleClose = () => {
    setName('')
    setTemplateId('')
    setSelectedUserIds(new Set())
    setSearchQuery('')
    setDebouncedSearch('')
    setUserPage(1)
    onClose()
  }

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Por favor ingresa un nombre para la campaña')
      return
    }
    if (!templateId) {
      toast.error('Por favor selecciona un template')
      return
    }
    if (selectedUserIds.size === 0) {
      toast.error('Por favor selecciona al menos un usuario')
      return
    }

    createMutation.mutate({
      name: name.trim(),
      template_id: templateId,
      user_ids: Array.from(selectedUserIds),
    })
  }

  // Usuarios de la página actual (ya filtrados server-side a role=platform + búsqueda)
  const users = (usersData?.users || []).filter((u) => u.email)
  const totalUsers = usersData?.total || 0
  const totalPages = usersData?.pages || 1
  const templates = templatesData?.templates || []

  const allCurrentSelected = users.length > 0 && users.every((u) => selectedUserIds.has(u.id))

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  // Selecciona/deselecciona solo los usuarios visibles en la página actual.
  const toggleAllCurrent = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (allCurrentSelected) {
        users.forEach((u) => next.delete(u.id))
      } else {
        users.forEach((u) => next.add(u.id))
      }
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva Campaña de Email</DialogTitle>
          <DialogDescription>
            Selecciona un template y los usuarios que recibirán el email
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nombre de la campaña */}
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Nombre de la campaña</Label>
            <Input
              id="campaign-name"
              placeholder="Ej: Encuesta Q1 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Template selector */}
          <div className="space-y-2">
            <Label>Template de Email</Label>
            {templatesLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} - {t.subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* User selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Usuarios ({selectedUserIds.size} seleccionados)</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAllCurrent}
                disabled={users.length === 0}
              >
                {allCurrentSelected ? 'Deseleccionar página' : 'Seleccionar página'}
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                aria-label="Buscar usuarios por nombre o email"
              />
            </div>

            {usersLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto rounded-md border">
                  {users.length === 0 ? (
                    <p className="p-4 text-center text-sm text-muted-foreground">
                      No se encontraron usuarios
                    </p>
                  ) : (
                    <div className="divide-y">
                      {users.map((user) => (
                        <label
                          key={user.id}
                          className="flex cursor-pointer items-center gap-3 p-3 hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={selectedUserIds.has(user.id)}
                            onCheckedChange={() => toggleUser(user.id)}
                            aria-label={`Seleccionar ${user.email}`}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {user.name} {user.lastname || ''}
                            </p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
                  <span>
                    {users.length} de {totalUsers} usuarios{debouncedSearch ? ' (filtrados)' : ''}
                  </span>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                        disabled={userPage === 1}
                      >
                        Anterior
                      </Button>
                      <span>
                        Pág {userPage}/{totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUserPage((p) => Math.min(totalPages, p + 1))}
                        disabled={userPage === totalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Enviando...' : 'Enviar Campaña'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
